import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Input, ModalWindow, RadioGroup, RadioGroupOption, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { FileDropZone } from './file_drop_zone';
import { TEMPLATE_DATA_DAMAGED_MESSAGE } from '../utils/consent_publish_validation';
import { isDocxFile, isPdfFile } from '../utils/consent_upload_file.helpers';
import { CONSENT_DOCX_CONVERSION_NOTICE, consentConversionFailureMessage, isConsentConversionError, } from '../utils/convert_consent_docx';
import { readPdfPageMetrics } from '../utils/pdf_page_metrics';
const RadioChoiceCard = ({ selected, children }) => (_jsx("div", { className: selected
        ? 'nucleus-box-border nucleus-flex nucleus-w-full nucleus-flex-col nucleus-rounded-lg nucleus-border-2 nucleus-border-solid nucleus-border-primary-500 nucleus-bg-informative-50'
        : 'nucleus-box-border nucleus-flex nucleus-w-full nucleus-flex-col nucleus-rounded-lg nucleus-border nucleus-border-solid nucleus-border-neutral-200 nucleus-bg-background', children: children }));
export const NewConsentVersionModal = ({ isOpen, templateId, sourceFile, sourceDocument, loadSourceDocument, cloneTemplateVersion, onClose, onVersionCreated, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [hydratedSource, setHydratedSource] = useState(null);
    const [sourceLoadFailed, setSourceLoadFailed] = useState(false);
    const resolvedSource = hydratedSource ?? sourceDocument ?? null;
    const parentVersion = sourceFile?.version ?? 0;
    const parentName = resolvedSource?.name ?? sourceFile?.title ?? '';
    const parentPages = resolvedSource?.totalPages ?? 0;
    const roleCount = resolvedSource?.roles.length ?? 0;
    const elementCount = resolvedSource?.elements.length ?? 0;
    const nextVersion = parentVersion > 0 ? parentVersion + 1 : 1;
    const [versionName, setVersionName] = useState(parentName);
    const [documentChoice, setDocumentChoice] = useState('same');
    const [configurationChoice, setConfigurationChoice] = useState('keep');
    const [selectedFile, setSelectedFile] = useState(null);
    const [stagedPageCount, setStagedPageCount] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        if (!isOpen) {
            setHydratedSource(null);
            setSourceLoadFailed(false);
            return;
        }
        setVersionName(sourceFile?.title ?? sourceDocument?.name ?? '');
        setDocumentChoice('same');
        setConfigurationChoice('keep');
        setSelectedFile(null);
        setStagedPageCount(null);
        setIsSubmitting(false);
    }, [isOpen, sourceFile?.title, sourceDocument?.name]);
    useEffect(() => {
        if (!isOpen || !templateId || !loadSourceDocument) {
            return;
        }
        let cancelled = false;
        void loadSourceDocument(templateId)
            .then((document) => {
            if (!cancelled) {
                if (document) {
                    setHydratedSource(document);
                }
                else {
                    setSourceLoadFailed(true);
                }
            }
        })
            .catch(() => {
            if (!cancelled) {
                setSourceLoadFailed(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, templateId, loadSourceDocument]);
    const handleClose = () => {
        setSelectedFile(null);
        setStagedPageCount(null);
        setIsSubmitting(false);
        onClose();
    };
    const handleFileSelected = async (files) => {
        const file = files[0];
        if (!isPdfFile(file) && !isDocxFile(file)) {
            addNotification({
                type: 'error',
                title: translate('Unsupported file type'),
                subtitle: translate('Only PDF or Word files are supported for a new version.'),
            });
            return;
        }
        setSelectedFile(file);
        // Word uploads skip the local page-count read; conversion happens later on Create,
        // so the "N pages · vX had M" warning only appears for PDF replacements.
        if (!isPdfFile(file)) {
            setStagedPageCount(null);
            return;
        }
        try {
            const metrics = await readPdfPageMetrics(file);
            setStagedPageCount(metrics.pageCount);
        }
        catch {
            setStagedPageCount(null);
        }
    };
    const canCreate = useMemo(() => {
        if (!templateId || isSubmitting || !versionName.trim()) {
            return false;
        }
        if (documentChoice === 'upload' && !selectedFile) {
            return false;
        }
        return true;
    }, [templateId, isSubmitting, versionName, documentChoice, selectedFile]);
    const footerHint = useMemo(() => {
        if (documentChoice === 'upload' && !selectedFile) {
            return translate('Upload a document to continue.');
        }
        if (documentChoice === 'upload' && selectedFile && configurationChoice === 'keep') {
            return translate('{count} elements will need review.', { count: elementCount });
        }
        return translate('Nothing is sent until you publish.');
    }, [documentChoice, selectedFile, configurationChoice, elementCount, translate]);
    const handleCreate = async () => {
        if (!templateId || !canCreate) {
            return;
        }
        setIsSubmitting(true);
        try {
            const document = await cloneTemplateVersion(templateId, {
                file: documentChoice === 'upload' ? (selectedFile ?? undefined) : undefined,
                keepConfiguration: configurationChoice === 'keep',
                name: versionName.trim(),
                sourceVersion: parentVersion,
                sourceName: parentName,
            });
            if (documentChoice === 'upload' && selectedFile && isDocxFile(selectedFile)) {
                addNotification({
                    type: 'success',
                    title: translate('{fileName} was converted to PDF', { fileName: selectedFile.name }),
                    subtitle: translate('A draft consent template was created.'),
                });
            }
            if (document.carryForward?.failed) {
                addNotification({
                    type: 'warning',
                    title: translate('Draft created'),
                    subtitle: translate('The configuration could not be copied. The draft was created with no elements.'),
                });
            }
            setSelectedFile(null);
            onVersionCreated(document.id);
        }
        catch (error) {
            const isConflict = error?.response?.status === 409;
            const errorCode = error?.code;
            const uploadedFile = documentChoice === 'upload' ? selectedFile : null;
            addNotification({
                type: 'error',
                title: uploadedFile
                    ? translate('New version from {fileName} failed', { fileName: uploadedFile.name })
                    : translate('New version failed'),
                subtitle: isConsentConversionError(error)
                    ? translate('{conversionFailure} A consent template was not created.', {
                        conversionFailure: translate(consentConversionFailureMessage(error)),
                    })
                    : isConflict
                        ? translate('A Draft version already exists for this template. Edit, publish, or delete it first.')
                        : errorCode === 'ROLLBACK_FAILED'
                            ? translate('Failed to attach the uploaded file to the new version, and failed to clean up the partial clone. Contact support.')
                            : errorCode === 'TEMPLATE_DATA_INVALID'
                                ? translate(TEMPLATE_DATA_DAMAGED_MESSAGE)
                                : translate('Unknown error'),
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const configurationStillLoading = Boolean(loadSourceDocument && isOpen && !hydratedSource && !sourceLoadFailed);
    const sameDocumentLabel = configurationStillLoading || parentPages === 0
        ? parentName || translate('Current document')
        : `${parentName || translate('Current document')} · ${parentPages} ${translate('pages')}`;
    const keepConfigurationDescription = configurationStillLoading
        ? translate('Loading configuration…')
        : sourceLoadFailed
            ? translate('Could not load configuration')
            : translate('{roleCount} roles and {elementCount} elements are copied into the draft', {
                roleCount,
                elementCount,
            });
    return (_jsx(ModalWindow, { isOpen: isOpen, closeWindow: handleClose, title: translate('Create a new version'), subtitle: translate('Draft v{next} — version {current} stays published until you publish v{next}.', {
            next: String(nextVersion),
            current: String(parentVersion || nextVersion - 1),
        }), hideCloseIcon: false, width: 'base', dataTestId: 'new-consent-version-modal', footerClassNames: 'nucleus-bg-neutral-50', footerStartContent: _jsx("div", { "data-testid": 'new-consent-version-footer-hint', children: _jsx(Text, { as: 'p', size: 'sm', className: 'nucleus-text-text-secondary', children: footerHint }) }), footerSecondaryActionButtons: [
            { label: translate('Cancel'), onClick: handleClose, disabled: isSubmitting, variant: 'transparent' },
        ], footerPrimaryActionButton: {
            label: translate('Create'),
            disabled: !canCreate,
            onClick: () => void handleCreate(),
            dataTestId: 'new-consent-version-create',
        }, children: _jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-gap-6', children: [_jsx(Input, { id: 'new-consent-version-name', label: translate('Version name'), width: 'full', value: versionName, onChange: (event) => setVersionName(event.target.value), dataTestId: 'new-consent-version-name' }), _jsx(RadioGroup, { label: translate('Document'), value: documentChoice, onChange: (value) => {
                        setDocumentChoice(value);
                        if (value === 'same') {
                            setSelectedFile(null);
                            setStagedPageCount(null);
                        }
                    }, dataTestId: 'new-consent-version-document', children: _jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-gap-2', children: [_jsx(RadioChoiceCard, { selected: documentChoice === 'same', children: _jsx("div", { className: 'nucleus-p-6', children: _jsx(RadioGroupOption, { option: {
                                            value: 'same',
                                            label: translate('Use the same document'),
                                            description: documentChoice === 'same' ? sameDocumentLabel : undefined,
                                            dataTestId: 'new-consent-version-document-same',
                                        } }) }) }), _jsxs(RadioChoiceCard, { selected: documentChoice === 'upload', children: [_jsx("div", { className: documentChoice === 'upload' ? 'nucleus-px-6 nucleus-pb-2 nucleus-pt-6' : 'nucleus-p-6', children: _jsx(RadioGroupOption, { option: {
                                                value: 'upload',
                                                label: translate('Upload a new document'),
                                                dataTestId: 'new-consent-version-document-upload',
                                            } }) }), documentChoice === 'upload' && !selectedFile ? (_jsx("div", { className: 'nucleus-px-6 nucleus-pb-4', children: _jsx(FileDropZone, { onUpload: handleFileSelected, title: translate('Add files here or click to upload'), subtitle: translate('.pdf or .docx, up to {maxFileSize} MB each. {conversionNotice}', {
                                                maxFileSize: 25,
                                                conversionNotice: translate(CONSENT_DOCX_CONVERSION_NOTICE),
                                            }), acceptedFileTypes: '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document', acceptedExtensions: ['PDF', 'DOCX'], maxFiles: 1, maxFileSize: 25, compact: true }) })) : null, documentChoice === 'upload' && selectedFile ? (_jsx("div", { className: 'nucleus-px-6 nucleus-pb-4', children: _jsxs("div", { className: 'nucleus-flex nucleus-items-center nucleus-gap-3 nucleus-rounded-lg nucleus-border nucleus-border-solid nucleus-border-neutral-200 nucleus-bg-background nucleus-px-3 nucleus-py-2', "data-testid": 'new-consent-version-staged-file', children: [_jsx(Icon, { name: 'checkCircleSolid', size: 'sm', color: 'green' }), _jsxs("div", { className: 'nucleus-min-w-0 nucleus-flex-1', children: [_jsx(Text, { as: 'p', size: 'sm', className: 'nucleus-font-medium', children: selectedFile.name }), stagedPageCount !== null ? (_jsx(Text, { as: 'p', size: 'xs', className: 'nucleus-text-text-warning', children: translate('{newPages} pages  ·  v{parentVersion} had {parentPages}', {
                                                                newPages: stagedPageCount,
                                                                parentVersion,
                                                                parentPages,
                                                            }) })) : null] }), isSubmitting ? (_jsx(Spinner, { size: 'sm' })) : (_jsx(Button, { variant: 'transparent', size: 'sm', label: translate('Remove'), iconName: 'delete', onClick: () => {
                                                        setSelectedFile(null);
                                                        setStagedPageCount(null);
                                                    }, dataTestId: 'new-consent-version-remove-file' }))] }) })) : null] })] }) }), _jsx(RadioGroup, { label: translate('Template Configuration'), value: configurationChoice, onChange: setConfigurationChoice, dataTestId: 'new-consent-version-configuration', children: _jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-gap-2', children: [_jsx(RadioChoiceCard, { selected: configurationChoice === 'keep', children: _jsx("div", { className: 'nucleus-p-6', children: _jsx(RadioGroupOption, { option: {
                                            value: 'keep',
                                            label: translate('Keep the current configuration'),
                                            description: keepConfigurationDescription,
                                            dataTestId: 'new-consent-version-configuration-keep',
                                        } }) }) }), _jsx(RadioChoiceCard, { selected: configurationChoice === 'fresh', children: _jsx("div", { className: 'nucleus-p-6', children: _jsx(RadioGroupOption, { option: {
                                            value: 'fresh',
                                            label: translate('Start fresh'),
                                            dataTestId: 'new-consent-version-configuration-fresh',
                                        } }) }) })] }) })] }) }));
};
